import React, { useEffect, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { DollarSign, TrendingUp, Calendar, Layers } from "lucide-react";
import { api } from "@/lib/api";

// Module-level constants — recharts re-renders the chart sub-tree on every new
// object reference for `margin` / `contentStyle` / `labelStyle`. Lifting them
// out of the component body keeps the chart children stable across renders.
const CHART_MARGIN = { top: 5, right: 12, left: 8, bottom: 5 };
const CHART_SIZE = { width: "100%", height: 220 };
const TOOLTIP_CONTENT_STYLE = {
  background: "#09090b",
  border: "1px solid #3f3f46",
  borderRadius: 2,
  fontSize: 12,
};
const TOOLTIP_LABEL_STYLE = { color: "#a1a1aa", fontFamily: "monospace" };
const BAR_RADIUS = [2, 2, 0, 0];

function tooltipFormatter(value, key, usd) {
  return key === "cost_usd" ? [usd(value), "Cost"] : [value, "Articles"];
}

/**
 * Costs tab — AI rewrite spend over time for a single site.
 *
 * Data source: GET /api/sites/{id}/costs. The per-article ai_cost_usd is
 * persisted forever, so we have a full historical record independent of the
 * Site.ai_spend_today_usd counter (which resets at midnight UTC).
 */
export default function CostsTab({ site }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setErr("");
    api.get(`/sites/${site.id}/costs`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(e.response?.data?.detail || e.message); });
    return () => { cancelled = true; };
  }, [site.id]);

  if (err) {
    return <div data-testid="costs-error" className="text-sm text-red-400 font-mono">{err}</div>;
  }
  if (!data) {
    return <div data-testid="costs-loading" className="text-sm text-zinc-500 font-mono">Loading cost data…</div>;
  }

  const t = data.totals;
  const usd = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;

  return (
    <div data-testid="costs-tab" className="space-y-6">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi tid="cost-lifetime" label="Lifetime spend" value={usd(t.lifetime_usd)} icon={Layers} accent="#fafafa" />
        <Kpi tid="cost-month" label="This month" value={usd(t.this_month_usd)} icon={Calendar} accent="#f59e0b" />
        <Kpi tid="cost-30d" label="Last 30 days" value={usd(t.last_30d_usd)} icon={TrendingUp} accent="#10b981" />
        <Kpi tid="cost-today" label="Today" value={usd(t.today_usd)} icon={DollarSign} accent="#fafafa" />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 gap-3">
        <Kpi tid="cost-articles-total" label="Articles rewritten (lifetime)"
             value={Number(t.articles_total).toLocaleString()} icon={Layers} small />
        <Kpi tid="cost-avg-per-article" label="Avg cost per article"
             value={usd(t.avg_cost_per_article_usd)} icon={DollarSign} small />
      </div>

      {/* Daily chart — last 30 days */}
      <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm" data-testid="cost-daily-chart">
        <div className="px-5 py-3 border-b border-zinc-800 flex items-baseline justify-between">
          <div className="label-tiny">Daily spend — last 30 days</div>
          <div className="text-[11px] text-zinc-500 font-mono">{usd(t.last_30d_usd)} total</div>
        </div>
        <div className="px-3 py-4" style={CHART_SIZE}>
          <ResponsiveContainer>
            <LineChart data={data.daily} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="2 4" stroke="#27272a" vertical={false} />
              <XAxis
                dataKey="date"
                stroke="#52525b" fontSize={10}
                tickFormatter={(d) => d.slice(5)}
                tickLine={false} axisLine={false}
                interval={4}
              />
              <YAxis
                stroke="#52525b" fontSize={10}
                tickFormatter={(v) => `$${v.toFixed(2)}`}
                tickLine={false} axisLine={false} width={50}
              />
              <Tooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                formatter={(v, k) => tooltipFormatter(v, k, usd)}
              />
              <Line type="monotone" dataKey="cost_usd" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly bar chart */}
      <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm" data-testid="cost-monthly-chart">
        <div className="px-5 py-3 border-b border-zinc-800 flex items-baseline justify-between">
          <div className="label-tiny">Monthly spend — last 12 months</div>
          <div className="text-[11px] text-zinc-500 font-mono">{usd(t.lifetime_usd)} lifetime</div>
        </div>
        <div className="px-3 py-4" style={CHART_SIZE}>
          <ResponsiveContainer>
            <BarChart data={data.monthly} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="2 4" stroke="#27272a" vertical={false} />
              <XAxis dataKey="month" stroke="#52525b" fontSize={10}
                     tickFormatter={(m) => m.slice(5)}  /* MM */
                     tickLine={false} axisLine={false} />
              <YAxis stroke="#52525b" fontSize={10}
                     tickFormatter={(v) => `$${v.toFixed(2)}`}
                     tickLine={false} axisLine={false} width={50} />
              <Tooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                formatter={(v, k) => tooltipFormatter(v, k, usd)}
              />
              <Bar dataKey="cost_usd" fill="#10b981" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly table for the operator who wants exact numbers */}
      <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm overflow-hidden" data-testid="cost-monthly-table">
        <div className="px-5 py-3 border-b border-zinc-800 label-tiny">Monthly breakdown</div>
        <div className="divide-y divide-zinc-800">
          {data.monthly.slice().reverse().map((m) => (
            <div key={m.month} data-testid={`cost-monthly-row-${m.month}`}
                 className="px-5 py-2 flex items-center justify-between text-sm">
              <div className="font-mono text-zinc-300">{m.month}</div>
              <div className="flex items-center gap-6">
                <div className="text-[11px] text-zinc-500 font-mono tabular-nums">{m.articles.toLocaleString()} articles</div>
                <div className="font-mono text-zinc-100 tabular-nums w-20 text-right">{usd(m.cost_usd)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-[10px] text-zinc-600 font-mono">
        Generated {new Date(data.generated_at).toLocaleString()} · Lifetime average:
        {" "}{usd(t.avg_cost_per_article_usd)}/article
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, tid, small }) {
  return (
    <div data-testid={tid} className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-4 flex items-start gap-3">
      <div className="p-1.5 border border-zinc-800 rounded-sm">
        <Icon size={14} strokeWidth={1.75} style={{ color: accent || "#a1a1aa" }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="label-tiny">{label}</div>
        <div
          className={`mt-1 font-display font-black tabular-nums ${small ? "text-lg" : "text-2xl"} truncate`}
          style={{ color: accent || "#fafafa" }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}
